import type {
    AddToCartButton,
    FBTProduct,
} from '@app/pdp/components/FrequentlyBoughtTogether/types';
import { type ProductHit } from '@libs/algolia';
import { logger } from '@libs/core';
import apiProxyClient from '@libs/api-proxy-client';

export const getStockInfoForIDs = async (ids: number[], storeId: number) => {
    try {
        const storesku = ids.map(sku => ({
            sku: `${sku}`,
            store: `${storeId}`,
        }));

        const inventoriesResponse =
            await apiProxyClient.inventory.getProductInventoryPairs(storesku);

        return (
            inventoriesResponse?.reduce<Record<string, boolean>>(
                (result, { sku, ...stores }) => {
                    const hasInventory = Object.values(stores).some(
                        inventory => (inventory as number) >= 1
                    );
                    if (hasInventory) {
                        result[sku] = true;
                    }
                    return result;
                },
                {}
            ) ?? {}
        );
    } catch (error) {
        logger.error('getStockInfoForIDs', JSON.stringify(error));

        return {};
    }
};

export const mapProductRecommendations = async (
    productRecommendations: ProductHit[],
    storeID: number | string
): Promise<FBTProduct[]> => {
    const productIds = productRecommendations.map(product => product.id);
    const parsedStoreId =
        typeof storeID === 'string' ? parseInt(storeID, 10) : storeID;

    const stockTable = await getStockInfoForIDs(productIds, parsedStoreId);

    return productRecommendations.map(product => ({
        ...product,
        in_stock: stockTable[product.id] ?? false,
        // Preselect each product if it's in stock for FBT
        isSelected: stockTable[product.id] ?? false,
    }));
};

export const setAddToCartButtonText = (
    addToCartData: AddToCartButton,
    products: ProductHit[]
) => {
    const { quantity } = addToCartData;

    if (quantity === 1) {
        return 'Add to pickup';
    }
    if (quantity === products.length) {
        return 'Add all to pickup';
    }

    return 'Add both to pickup';
};
